/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { clsx } from "clsx"
import { ChevronDownIcon, LogOutIcon, SettingsIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router"

import { SessionViewModel } from "../../features/session/session-view-model"
import { AppRoutes } from "../../routes/routes"
import { forceLoginRedirect } from "../../utils/force-redirect"
import { useActiveRoute } from "../../utils/use-active-route"
import { AdminAccess, SuperAdminAccess } from "../app/admin-access"
import { useAppState, useViewModel } from "../app/app-hooks"
import { DeveloperAccess } from "../app/developer-access"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from "../primitives/dropdown-menu"

import { SidebarLinkView } from "./sidebar-link-view"
import "./sidebar-view.css"
import { SidebarOffcanvasView } from "./sidebar-offcanvas-view"

const cssForActiveRoute = (current: AppRoutes, activeRoute: AppRoutes | null): string =>
    current === activeRoute ? "active" : "text-white"

const SIDEBAR_ANIMATED_KEY = "sidebar-animated"

export const SidebarView = observer(() => {
    const sessionVM = useViewModel(SessionViewModel)
    const activeRoute = useActiveRoute()
    const navigate = useNavigate()
    const appState = useAppState()

    const [isOffcanvasOpened, setOffcanvasOpened] = useState(false)
    const [shouldAnimateIn, setShouldAnimateIn] = useState(false)
    const [shouldAnimateOut, setShouldAnimateOut] = useState(false)

    // stable identity: the offcanvas registers a keydown listener keyed on this.
    const closeOffcanvas = useCallback(() => setOffcanvasOpened(false), [])

    useEffect(() => {
        const hasAnimatedIn = sessionStorage.getItem(SIDEBAR_ANIMATED_KEY)
        if (!hasAnimatedIn) {
            setShouldAnimateIn(true)
            sessionStorage.setItem(SIDEBAR_ANIMATED_KEY, "true")
        }
    }, [])

    const logout = async () => {
        // reset the animated state
        sessionStorage.removeItem(SIDEBAR_ANIMATED_KEY)
        // show content loader
        appState.setRouteLoading(true)
        // animate sidebar out
        setShouldAnimateOut(true)
        setTimeout(async () => {
            await sessionVM.logout()
            forceLoginRedirect()
        }, 200) // a little shorter than the animation, so the logout request is faster
    }

    const confirmAndLogout = async () => {
        if (window.confirm("Are you sure you want to log out?")) {
            await logout()
        }
    }

    return (
        <>
            <div
                className={clsx("sidebar-view flex shrink-0 flex-col rounded-legacy-lg p-legacy shadow-legacy", {
                    "sidebar-slide-in": shouldAnimateIn,
                    "sidebar-slide-out": shouldAnimateOut
                })}
            >
                <a href="/" className="flex items-center text-white no-underline">
                    <span className="me-1">
                        <img src="/assets/iap-logo.svg" alt="In-App Protection logo" width={30} />
                    </span>
                    <h3 className={"font-bold mb-2 mt-2"}>In-App Protection</h3>
                </a>
                <hr />
                <ul className="nav nav-pills mb-auto flex-col">
                    <li className={"mt-2"}>
                        <h6 className="text-header px-legacy uppercase">Threat Insights</h6>
                    </li>
                    <SidebarLinkView
                        to={AppRoutes.Dashboard}
                        title="Dashboard"
                        iconPath="/assets/ic-grid-light.svg"
                        css={cssForActiveRoute(AppRoutes.Dashboard, activeRoute)}
                    />

                    <li className={"mt-6"}>
                        <h6 className="text-header px-legacy uppercase">Settings</h6>
                    </li>
                    <li className={`nav-item ${isOffcanvasOpened ? "offcanvas-opened" : ""}`}>
                        <a
                            className={"applications-nav nav-link text-white"}
                            href="#sidebarOffcanvas"
                            role="button"
                            onClick={(evt) => {
                                evt.preventDefault()
                                evt.stopPropagation()
                                setOffcanvasOpened(!isOffcanvasOpened)
                            }}
                        >
                            <img src="/assets/ic-window-light.svg" className="mb-1" alt={"Applications"} />
                            <span className="ms-legacy">Applications</span>
                        </a>
                    </li>
                    <DeveloperAccess>
                        <SidebarLinkView
                            to={AppRoutes.GetSDK}
                            title="Get the SDK"
                            iconPath="/assets/ic-boxes-light.svg"
                            css={cssForActiveRoute(AppRoutes.GetSDK, activeRoute)}
                        />
                    </DeveloperAccess>
                    <SidebarLinkView
                        to={AppRoutes.Logs}
                        title="Errors"
                        iconPath="/assets/ic-journal-x-light.svg"
                        css={cssForActiveRoute(AppRoutes.Logs, activeRoute)}
                    />

                    <AdminAccess>
                        <li className={"mt-6"}>
                            <h6 className="text-header px-legacy uppercase">Administration</h6>
                        </li>

                        <SuperAdminAccess>
                            <SidebarLinkView
                                to={AppRoutes.AllOrganizations}
                                title="All Organizations"
                                iconPath="/assets/ic-diagram-3-light.svg"
                                css={cssForActiveRoute(AppRoutes.AllOrganizations, activeRoute)}
                            />
                        </SuperAdminAccess>
                        <SidebarLinkView
                            to={AppRoutes.AllApplicationTypes}
                            title="All Applications"
                            iconPath="/assets/ic-window-stack-light.svg"
                            css={cssForActiveRoute(AppRoutes.AllApplicationTypes, activeRoute)}
                        />
                        <SidebarLinkView
                            to={AppRoutes.AllUsers}
                            title="All Users"
                            iconPath="/assets/ic-person-lines-fill-light.svg"
                            css={cssForActiveRoute(AppRoutes.AllUsers, activeRoute)}
                        />
                    </AdminAccess>
                </ul>
                <hr />
                <DropdownMenu>
                    <DropdownMenuTrigger className="flex cursor-pointer items-center text-white">
                        <div className="avatar me-2 rounded-legacy-circle p-2">
                            <img src="/assets/ic-person-light.svg" alt="" width="24" height="24" />
                        </div>

                        <strong>{sessionVM.currentUser?.username}</strong>
                        <ChevronDownIcon className="ms-2 size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="dark ring-0">
                        <DropdownMenuGroup>
                            <DropdownMenuLabel className="text-sm">{sessionVM.currentUser?.name}</DropdownMenuLabel>
                            <DropdownMenuItem
                                className="cursor-pointer text-base"
                                onClick={() => navigate(AppRoutes.Settings)}
                            >
                                <SettingsIcon />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer text-base" onClick={() => confirmAndLogout()}>
                                <LogOutIcon />
                                Log out
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <SidebarOffcanvasView isOpened={isOffcanvasOpened} onClose={closeOffcanvas} />
        </>
    )
})
